// /verify-email — post-signup waiting screen for email confirmation.
//
// Visual + copy from
//   design/UI : UX v1.0/for-engineering-auth/specs/screen-strings.md § 7.
//
// Phase 0 status: NOT wired into the active admin invite flow (we
// admin-create with email_verified=true, so this screen is bypassed for
// internal admin users). Built per spec for Phase 1 consumer signup, where
// users self-register and need to click a verification link.
//
// Two states represented:
//  - Waiting:    "we sent a link, click it"  — polling stub for verification
//  - Verifying:  "verifying…"                — when arriving via ?token=… link
//  - Success:    "email verified"            — terminal success
//  - Error:      "verification failed"       — link expired/invalid
//
// The polling and the verify-token Cognito call are stubbed (TODO Phase 1).
// The UI shell is complete + spec-accurate.

import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Card,
  EmailIcon,
  LangToggle,
  Spinner,
} from '@gc-coach/design-system';
import { AdminAuthShell } from '../../auth/index.js';
import { useI18n } from '../../i18n/index.js';
import styles from './AuthScreens.module.css';

const RESEND_COOLDOWN_SECONDS = 60;

type VerifyState =
  | { kind: 'waiting' }
  | { kind: 'verifying' }
  | { kind: 'success' }
  | { kind: 'error' };

export function EmailVerifyScreen() {
  const { t, tt, lang, setLang } = useI18n();
  const [searchParams] = useSearchParams();

  const email = searchParams.get('email') ?? '';
  const token = searchParams.get('token');

  // Initial state: verifying if a token is in the URL, waiting otherwise.
  const initial = useMemo<VerifyState>(
    () => (token ? { kind: 'verifying' } : { kind: 'waiting' }),
    [token],
  );
  const [state, setState] = useState<VerifyState>(initial);

  // Resend countdown.
  const [resendIn, setResendIn] = useState(RESEND_COOLDOWN_SECONDS);
  useEffect(() => {
    if (state.kind !== 'waiting' || resendIn === 0) return;
    const id = window.setInterval(() => {
      setResendIn((s) => Math.max(0, s - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [state.kind, resendIn]);

  // Phase 1 TODO: when ?token=... is present, call Cognito to verify it.
  // For now, just sit in the verifying state (visual demonstration only).
  useEffect(() => {
    if (state.kind !== 'verifying') return;
    // Placeholder — wire to Cognito VerifyUserAttribute (or ConfirmSignUp,
    // depending on the consumer-signup flow chosen in Phase 1) and transition
    // to success / error based on response.
  }, [state.kind]);

  function onResend() {
    if (resendIn > 0) return;
    // Phase 1 TODO: call Cognito ResendConfirmationCode here.
    setResendIn(RESEND_COOLDOWN_SECONDS);
  }

  return (
    <AdminAuthShell>
      <div className={styles.topBar}>
          <LangToggle value={lang} onChange={setLang} />
        </div>

        <div className={styles.formColumn}>
          <header className={styles.header}>
            <span className={styles.heroIcon} aria-hidden="true">
              <EmailIcon width={32} height={32} />
            </span>
            <span className={styles.eyebrow}>{t('emailVerify.eyebrow')}</span>
            <h1 className={styles.title}>
              {state.kind === 'success'
                ? t('emailVerify.successHeadline')
                : state.kind === 'error'
                  ? t('emailVerify.errorHeadline')
                  : t('emailVerify.headline')}
            </h1>
          </header>

          <Card>
            {state.kind === 'waiting' && (
              <div className={styles.form}>
                <p className={styles.body}>
                  {tt('emailVerify.bodyTemplate', { email })}
                </p>

                <div className={styles.statusPanel} aria-live="polite">
                  <Spinner />
                  <span className={styles.statusText}>
                    {t('emailVerify.waitingStatus')}
                  </span>
                  <span className={styles.statusCaption}>
                    {t('emailVerify.waitingCaption')}
                  </span>
                </div>

                <div className={styles.resendRow}>
                  <span className={styles.resendPrefix}>
                    {t('emailVerify.resendPrefix')}
                  </span>
                  {resendIn > 0 ? (
                    <span className={styles.resendCountdown}>
                      {tt('emailVerify.resendCountdownTemplate', { seconds: resendIn })}
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={onResend}
                      className={styles.inlineLink}
                    >
                      {t('emailVerify.resendButton')}
                    </button>
                  )}
                </div>

                <Link to="/login" className={styles.inlineLink}>
                  {t('emailVerify.changeEmailLink')}
                </Link>
              </div>
            )}

            {state.kind === 'verifying' && (
              <div className={styles.statusPanel} aria-live="polite">
                <Spinner />
                <span className={styles.statusText}>
                  {t('emailVerify.verifyingStatus')}
                </span>
              </div>
            )}

            {state.kind === 'success' && (
              <div className={styles.form}>
                <p className={styles.body}>{t('emailVerify.successBody')}</p>
                <Link to="/login" className={styles.inlineLink}>
                  {t('emailVerify.successButton')}
                </Link>
              </div>
            )}

            {state.kind === 'error' && (
              <div className={styles.form}>
                <Alert severity="danger">{t('emailVerify.errorBody')}</Alert>
                <button
                  type="button"
                  onClick={() => setState({ kind: 'waiting' })}
                  className={styles.inlineLink}
                >
                  {t('emailVerify.errorRequestNew')}
                </button>
                <Link to="/login" className={styles.inlineLink}>
                  {t('forgotPassword.backLink')}
                </Link>
              </div>
            )}
          </Card>
        </div>
    </AdminAuthShell>
  );
}
